import { io, Socket } from 'socket.io-client'
import { EventEmitter } from 'events'
import _startCase from 'lodash/startCase'
import authentication from '../core/authentication'
import appConfig from '../core/config'

export interface SocketEventMessage {
  type: string
  data: any
}

export class Events {
  private socket!: Socket
  private emitter: EventEmitter

  constructor() {
    this.emitter = new EventEmitter()
    this.emitter.setMaxListeners(500)
  }

  private async authenticate() {
    const token = await authentication.getAccessToken()
    const isImpersonating = await authentication.isImpersonating()
    const obo = isImpersonating
      ? await authentication.getAccessToken(true)
      : undefined
    this.socket.emit('authenticate', {
      t: token,
      imp: isImpersonating,
      obo,
    })
  }

  async connect() {
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
    }

    return new Promise((resolve) => {
      const host = appConfig.websocketsHost
      this.socket = io(host, {
        path: '/events/',
        transports: ['websocket', 'polling'],
      })

      this.socket.on('connected', async (data) => {
        await this.authenticate()
        this.socket.on('authenticated', (data) => {
          if (data.success) {
            console.info('Socket authenticated')
          } else {
            console.error('Failed to authenticate socket')
          }
        })
        resolve(true)
      })

      this.socket.on('reply', (message) => {
        if (appConfig.isDevelopment || appConfig.logEvents) {
          console.info(message)
        }

        this.emitter.emit('event', message)
        this.emitter.emit(message.type, message)

        if (message.detail.model) {
          this.emitter.emit(message.detail.model, message)
        }
      })
    })
  }

  emit(message: SocketEventMessage) {
    this.socket.emit('send_message', message)
  }

  on(event: string, handler: (...args: any[]) => any) {
    this.socket?.on(event, handler)
  }

  off(event: string) {
    this.socket?.off(event)
  }

  disconnect() {
    this.socket?.disconnect()
  }

  addEventListener(event: string, handler: (event: any) => any) {
    this.emitter.addListener(event, handler)
  }

  removeEventListener(event: string, handler: (event: any) => any) {
    this.emitter.removeListener(event, handler)
  }

  clearListeners() {
    this.emitter.removeAllListeners()
  }

  async restart() {
    this.disconnect()
    await this.connect()
  }
}

export default new Events()
